/**
 * PURE composition: query rows → `ApplicationDetailData` pieces. No db, no
 * `.server` imports — every input arrives as plain data so each function is
 * unit-testable (load-application-detail.server.test.ts). v1 citations are
 * as of 4cbfe7e; quirks preserved per DETAIL-PAGE-V2-PARITY.md.
 */
import { z } from 'zod'
import type { MemberStepPill } from '#app/utils/application-detail-v2/vendor/card-kit'
import { GUARANTOR_APPLICANTS_NOT_READY_MARKER } from '#app/utils/application-detail-v2/vendor/guarantor-summary-recovery'
import { propertyDisplay } from '#app/utils/application-detail-v2/vendor/property-display'
import {
	type RelationshipInvitation,
	deriveApplicationStatus,
	deriveDisplayName,
	deriveDownloadAvailability,
	deriveRelationshipLabels,
} from './derive'
import { parseStepData } from './step-data-schemas'
import type {
	ApplicationCategory,
	ApplicationSummary,
	CreditSummary,
	DownloadAvailability,
	FamilyScoreSummary,
	GroupMember,
	ParsedStepData,
	PropertySummary,
	ScreeningKind,
	ScreeningResultsSummary,
} from './types'

/**
 * Collapse re-sent invitations for display (2026-07-17): resending to the
 * same address inserts a NEW pending/sent row, so the live-invite queries
 * return one row per send. Duplicates are keyed by invitation type +
 * case-insensitive email; the first row wins, salvaging a filled
 * relationship from any later duplicate. Rows without an email never
 * collapse — there is no way to tell they target the same person.
 */
export function dedupePendingInvitations<
	T extends {
		invitedEmail: string | null
		invitationType: string
		relationship: string | null
	},
>(invitations: T[]): T[] {
	const indexByKey = new Map<string, number>()
	const out: T[] = []
	for (const invitation of invitations) {
		const email = invitation.invitedEmail?.trim().toLowerCase()
		if (!email) {
			out.push(invitation)
			continue
		}
		const key = `${invitation.invitationType}:${email}`
		const existing = indexByKey.get(key)
		if (existing === undefined) {
			indexByKey.set(key, out.length)
			out.push(invitation)
			continue
		}
		if (!out[existing].relationship && invitation.relationship) {
			out[existing] = {
				...out[existing],
				relationship: invitation.relationship,
			}
		}
	}
	return out
}

/** v1 branches on `=== 'primary'` / `=== 'guarantor'` and routes everything
 * else down the co-applicant path — unknown legacy strings get that bucket. */
export function narrowApplicationType(
	raw: string,
): 'primary' | 'co_applicant' | 'guarantor' {
	return raw === 'primary' || raw === 'guarantor' ? raw : 'co_applicant'
}

/** Narrow DB text to the closed category enum (unknown → standard). */
export function narrowApplicationCategory(
	value: string | null | undefined,
): ApplicationCategory {
	return value === 'student_housing' ? 'student_housing' : 'standard'
}

/** The 6 modeled UI step types only; `tencheck` and `personal-info` stay OUT
 * of ParsedStepData by recorded decision (parity §5). */
export function parseDetailSteps(raw: Record<string, unknown>): ParsedStepData {
	return {
		property: parseStepData('property', raw.property),
		rental: parseStepData('rental', raw.rental),
		employment: parseStepData('employment', raw.employment),
		'verify-income': parseStepData('verify-income', raw['verify-income']),
		'id-verification': parseStepData('id-verification', raw['id-verification']),
		review: parseStepData('review', raw.review),
	}
}

export interface ResultsRow {
	overallScore: number | null
	eligibilityStatus: string | null
	aiProcessingStatus: string | null
	/** Optional: loaders pass the full drizzle row, which carries it. */
	aiProcessingError?: string | null
	updatedAt: Date | string | null
	guarantorRequired: boolean
	verificationStatus: Record<string, unknown> | null
	aiReportData: Record<string, unknown> | null
}

/** Slice of tenant_screening_results the page reads (v1:2034-2113). */
export function buildResultsSummary(
	row: ResultsRow | null | undefined,
): ScreeningResultsSummary | null {
	if (!row) return null
	return {
		overallScore: row.overallScore,
		eligibilityStatus: row.eligibilityStatus,
		aiProcessingStatus: row.aiProcessingStatus,
		aiProcessingUpdatedAt: row.updatedAt
			? new Date(row.updatedAt).toISOString()
			: null,
		guarantorRequired: row.guarantorRequired,
		verificationStatus: row.verificationStatus ?? null,
		aiReportData: row.aiReportData ?? null,
		// Parked guarantor: the summary is deliberately waiting for applicant
		// full reports (marker-prefixed message written by the AI worker) and
		// resumes automatically — the UI shows the waiting card instead of an
		// endless generating overlay. 'failed' covers legacy pre-parking rows.
		reportWaitingOnApplicants:
			(row.aiProcessingStatus === 'pending' ||
				row.aiProcessingStatus === 'failed') &&
			(row.aiProcessingError ?? '').startsWith(
				GUARANTOR_APPLICANTS_NOT_READY_MARKER,
			),
	}
}

export interface AdultContact {
	name?: string
	email?: string
	phone?: string
	relationship?: string
}

/** v1:1998-2010 — primary reads adults[0]; co-applicants/guarantors match
 * their account email (case-insensitive), falling back to adults[0]. Same
 * chain as deriveDisplayName, re-resolved here for the email/phone fields. */
export function resolveAdultContact(input: {
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
	adults: AdultContact[] | undefined
	userEmail: string | null
}): AdultContact | undefined {
	if (input.applicationType === 'primary') return input.adults?.[0]
	return (
		input.adults?.find(
			(adult) =>
				adult.email &&
				input.userEmail &&
				adult.email.toLowerCase() === input.userEmail.toLowerCase(),
		) ?? input.adults?.[0]
	)
}

export interface GroupMemberSource {
	applicationId: string
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
	rawStatus: string
	aiProcessingStatus: string | null
	/** Raw `screening_type` column — anything but 'pre_screening' (absent
	 * included) composes to the 'full' kind, the same narrowing the entry
	 * loaders use. */
	screeningType?: string | null
	score: number | null
	adults: AdultContact[] | undefined
	userEmail: string | null
	/** Account phone (Reach Out / Send Message); null when absent. */
	userPhone?: string | null
	userName: string | null
	username: string | null
	/** Veriff face photo URL (combined page); null when none → initials. */
	photoUrl: string | null
	/** results.guarantorRequired — drives the orange overview ring (§7.2). */
	guarantorRequired: boolean
	/** Guarantors only: their acceptability verdict (null = not judged). */
	guarantorAcceptable?: boolean | null
	/** Hover-trail pills (ID · Details · Income) — combined page only
	 * (2026-07-13); the individual switcher omits them. */
	steps?: MemberStepPill[]
}

/** Switcher row order (user directive 2026-06-13): primary on top,
 * co-applicants next, guarantors last; stable within each type. */
const MEMBER_TYPE_RANK: Record<GroupMemberSource['applicationType'], number> = {
	primary: 0,
	co_applicant: 1,
	guarantor: 2,
}

/** Applicant-switcher rows (parity §5/§10). `invitations` feed the
 * relationship labels first (2026-07-13 — the inviter records the relation
 * on the invite row; see deriveRelationshipLabels order ①-④). */
export function buildGroupMembers(
	sources: GroupMemberSource[],
	invitations: RelationshipInvitation[] = [],
): GroupMember[] {
	const labels = deriveRelationshipLabels(
		sources.map((source) => ({
			applicationId: source.applicationId,
			applicationType: source.applicationType,
			adults: source.adults,
			userEmail: source.userEmail,
		})),
		invitations,
	)
	const ordered = [...sources].sort(
		(a, b) =>
			MEMBER_TYPE_RANK[a.applicationType] - MEMBER_TYPE_RANK[b.applicationType],
	)
	return ordered.map((source) => ({
		applicationId: source.applicationId,
		displayName: deriveDisplayName({
			applicationType: source.applicationType,
			adults: source.adults,
			userEmail: source.userEmail,
			userName: source.userName,
			username: source.username,
		}),
		applicationType: source.applicationType,
		// v1 quirk preserved (parity §3): the label map is EMPTY when the group
		// has no primary, or the primary's property step has no adults array —
		// every member (primary included) then falls back to 'Co-Applicant'.
		relationshipLabel: labels.get(source.applicationId) ?? 'Co-Applicant',
		status: deriveApplicationStatus(
			source.rawStatus,
			source.aiProcessingStatus,
		),
		score: source.score,
		screeningKind:
			source.screeningType === 'pre_screening' ? 'pre_screening' : 'full',
		email: source.userEmail,
		phone: source.userPhone ?? null,
		photoUrl: source.photoUrl,
		guarantorRequired: source.guarantorRequired,
		guarantorAcceptable: source.guarantorAcceptable ?? null,
		steps: source.steps,
		// Family-report eligibility uses the raw AI status, NOT the derived
		// `status` above (an accepted member has a finished report but derives to
		// 'accepted', not 'completed'). Mirrors the route's notReady gate.
		reportCompleted: source.aiProcessingStatus === 'completed',
	}))
}

export interface ApplicationRowForSummary {
	id: string
	propertyId: string | null
	groupId: string | null
	applicationType: string
	status: string
	screeningScore: number | null
	createdAt: Date
	viewedAt: Date | null
	/** TT-12: set when the submission was accepted but the report is held
	 * because the landlord wallet was at/under its floor. */
	billingHoldAt?: Date | null
}

/** Core identity + landlord-decision fields (v1:2080-2123). */
export function buildApplicationSummary(input: {
	application: ApplicationRowForSummary
	/** The route param — v1:2082 falls back to it (`app.propertyId || propertyId`). */
	fallbackPropertyId: string
	results: ScreeningResultsSummary | null
	adults: AdultContact[] | undefined
	/** Pre-parsed by queries.server.ts `parsePersonalInfoPhone` (decision: the
	 * personal-info step never joins ParsedStepData; parity §5). */
	personalInfoPhone: string | null
	user:
		| {
				email: string | null
				name: string | null
				username: string | null
				phone: string | null
		  }
		| undefined
}): ApplicationSummary {
	const { application, results } = input
	const applicationType = narrowApplicationType(application.applicationType)
	const adult = resolveAdultContact({
		applicationType,
		adults: input.adults,
		userEmail: input.user?.email ?? null,
	})
	return {
		id: application.id,
		propertyId: application.propertyId ?? input.fallbackPropertyId,
		groupId: application.groupId ?? null,
		applicationType,
		derivedStatus: deriveApplicationStatus(
			application.status,
			results?.aiProcessingStatus,
		),
		applicantName: deriveDisplayName({
			applicationType,
			adults: input.adults,
			userEmail: input.user?.email ?? null,
			userName: input.user?.name ?? null,
			username: input.user?.username ?? null,
		}),
		applicantEmail: adult?.email || input.user?.email || null,
		// v1:2090-2095 — adult phone → personal-info step phone → account phone.
		applicantPhone:
			adult?.phone || input.personalInfoPhone || input.user?.phone || null,
		applicationDate: application.createdAt.toISOString(),
		score: results?.overallScore ?? application.screeningScore ?? null,
		guarantorRequired: results?.guarantorRequired ?? false,
		viewedAt: application.viewedAt ? application.viewedAt.toISOString() : null,
		billingHold: Boolean(application.billingHoldAt),
	}
}

export interface UnitFactsRow {
	bedrooms: number | null
	bathrooms: number | null
	/** Rounded legacy alias — kept as a fallback. */
	squareFeet: number | null
	/** The D19 decimal column; preferred so 1850.5 doesn't display as 1851. */
	livingAreaSqft: number | null
	monthlyRent: number | null
}

/** Header-strip facts. Address comes from the property row; beds/baths/rent
 * prefer the applicant's property step — what was actually applied for —
 * over the unit row (legacy step rows store monthlyRent as a string);
 * sqft exists only on the unit row. Bathrooms is collected in the Property
 * step popup, so it follows the same step-over-unit precedence as bedrooms. */
export function buildPropertySummary(input: {
	/** The live property record's name + address fields — the identity is
	 * derived here so the web report matches the PDFs and every other surface. */
	property: {
		propertyName?: string | null
		buildingName?: string | null
		formattedAddress?: string | null
		address?: string | null
	}
	unit: UnitFactsRow | null
	propertyStep: ParsedStepData['property']
}): PropertySummary {
	const stepRent = input.propertyStep?.monthlyRent
	const stepRentNumber =
		typeof stepRent === 'string' ? Number(stepRent) : (stepRent ?? null)
	return {
		title: propertyDisplay(input.property).title,
		address: input.property.formattedAddress || input.property.address || '',
		bedrooms: input.propertyStep?.bedrooms ?? input.unit?.bedrooms ?? null,
		bathrooms: input.propertyStep?.bathrooms ?? input.unit?.bathrooms ?? null,
		// Prefer the decimal column so a typed 1850.5 isn't shown as 1851.
		squareFeet: input.unit?.livingAreaSqft ?? input.unit?.squareFeet ?? null,
		unitNumber: input.propertyStep?.unitNumber ?? null,
		monthlyRent:
			stepRentNumber != null && Number.isFinite(stepRentNumber)
				? stepRentNumber
				: (input.unit?.monthlyRent ?? null),
	}
}

/** v1 renders the income/rent rows when the AI report's
 * `predictions.verificationSummary.<section>` is truthy
 * (report-data-helpers `deriveVerificationSummary`; parity §2). */
const AiReportSectionsSchema = z
	.object({
		predictions: z
			.object({
				verificationSummary: z
					.object({
						incomeVerification: z.unknown().optional(),
						rentVerification: z.unknown().optional(),
					})
					.passthrough()
					.optional()
					.catch(undefined),
			})
			.passthrough()
			.optional()
			.catch(undefined),
	})
	.passthrough()

function aiReportHasSection(
	aiReportData: Record<string, unknown> | null,
	section: 'incomeVerification' | 'rentVerification',
): boolean {
	if (!aiReportData) return false
	const parsed = AiReportSectionsSchema.safeParse(aiReportData)
	if (!parsed.success) return false
	return Boolean(parsed.data.predictions?.verificationSummary?.[section])
}

export function buildDownloadAvailability(input: {
	screeningKind: ScreeningKind
	isGroup: boolean
	credit: CreditSummary | null
	familyScore: FamilyScoreSummary | null
	aiReportData: Record<string, unknown> | null
}): DownloadAvailability {
	return deriveDownloadAvailability({
		screeningKind: input.screeningKind,
		isGroup: input.isGroup,
		hasFamilyScore: input.familyScore?.processingStatus === 'completed',
		// v1's credit condition: TenCheck data or a pre-generated blob exists
		// (parity §2).
		hasCreditReport:
			input.credit != null &&
			(input.credit.reportData != null || input.credit.blobPath != null),
		hasIncomeVerification: aiReportHasSection(
			input.aiReportData,
			'incomeVerification',
		),
		hasRentVerification: aiReportHasSection(
			input.aiReportData,
			'rentVerification',
		),
	})
}
